import type { NavigationGuardReturn, RouteLocationNormalized } from 'vue-router'
import { gateReasonForRoute, requestAuthForRoute } from '@/composables/useAuthGate'
import { useAuthStore } from '@/stores/auth'

/**
 * 存取控制。
 *
 * `requiresAuth` 未登入 → **不換頁**，就地開登入對話框
 * （`composables/useAuthGate.ts`），登入完成後自動把原本要去的地方補上。
 * `guestOnly` 已登入 → 導開，避免已登入的人還看得到登入表單。
 *
 * 為什麼不再導到 `/login?redirect=…`：那會把使用者從他正在看的畫面上帶走，
 * 而他可能只是想按一下存款。決定不登入的話還得自己按返回。
 *
 * 兩種情況要分開處理，差別在於**底下有沒有畫面可以襯托對話框**：
 *
 * - **站內導航**（已經有頁面）→ 取消這次導航，對話框浮在原地
 * - **直接輸網址／重新整理**（`from` 是 START_LOCATION，畫面是空的）
 *   → 送到首頁再開對話框。留在空白頁上開對話框的話，關掉之後
 *   使用者會盯著一片空白；首頁至少是一個可以繼續逛的地方
 */
export function authGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
): NavigationGuardReturn {
  const auth = useAuthStore()

  if (to.meta.requiresAuth === true && !auth.isAuthenticated) {
    requestAuthForRoute(gateReasonForRoute(String(to.name ?? '')), to.fullPath)

    // `matched` 是空的才代表這是本次工作階段的第一次導航。
    // 不能用 `from.name === undefined` —— 匿名路由也是 undefined
    return from.matched.length === 0 ? { name: 'home', replace: true } : false
  }

  if (to.meta.guestOnly === true && auth.isAuthenticated) {
    return { name: 'home', replace: true }
  }

  return true
}
